import React, { ReactNode, useState, useMemo } from 'react'

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'

import {
  restrictToParentElement,
  restrictToVerticalAxis,
  restrictToHorizontalAxis
} from '@dnd-kit/modifiers'

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy
} from '@dnd-kit/sortable'

import Box from 'components_new/atoms/Box'

import DndListItem from './DndListItem'
import DndSortableListItem from './DndSortableListItem'

interface DndListProps {
  editable: boolean
  itemGap: number
  itemHeight: number
  items: {
    disabled?: boolean
    id: string
    content: ReactNode
    containerSx?: object
  }[]
  handleDelete?: (id: string) => void
  handleDragEnd: (newOrder: string[]) => void
  horizontal?: boolean
  sx?: object
  variant?: 'filled' | 'outlined' | 'transparent'
}

const DndList = (props: DndListProps) => {
  const {
    editable,
    itemGap,
    itemHeight,
    items,
    handleDelete,
    handleDragEnd,
    horizontal = false,
    sx,
    variant
  } = props

  const [activeId, setActiveId] = useState<string | null>(null)
  const itemIds = useMemo(() => items.map((item) => item.id), [items])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  function handleDragStart(event: any) {
    const { active } = event

    if (editable) {
      setActiveId(active.id)
    }
  }

  const activeItem = items.find((item) => item.id === activeId)

  return (
    <Box sx={sx}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragCancel={() => setActiveId(null)}
        onDragEnd={(event: any) => {
          const { active, over } = event

          if (active.id !== over.id) {
            const oldIndex = itemIds.indexOf(active.id)
            const newIndex = itemIds.indexOf(over.id)
            const newItemIds = arrayMove(itemIds, oldIndex, newIndex)

            handleDragEnd(newItemIds)

            return newItemIds
          }

          return itemIds
        }}
        onDragStart={handleDragStart}
        modifiers={[
          horizontal ? restrictToHorizontalAxis : restrictToVerticalAxis
        ]}
      >
        <SortableContext
          items={itemIds}
          strategy={
            horizontal
              ? horizontalListSortingStrategy
              : verticalListSortingStrategy
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: horizontal ? 'row' : 'column',
              height: `${(itemHeight + itemGap) * items.length}px`,
              overflowY: 'auto',
              transition: 'all 0.2s ease'
            }}
          >
            {itemIds.map((id) => {
              const item = items.find((item) => item.id === id)

              return (
                <DndSortableListItem
                  editable={editable}
                  disabled={item?.disabled}
                  handleDelete={
                    handleDelete ? () => handleDelete(id) : undefined
                  }
                  id={id}
                  key={id}
                  sx={{
                    height: itemHeight,
                    mb: `${itemGap}px`,
                    ...item?.containerSx
                  }}
                  variant={variant}
                >
                  {item?.content}
                </DndSortableListItem>
              )
            })}
          </Box>
        </SortableContext>
        <DragOverlay modifiers={[restrictToParentElement]}>
          {activeId && activeItem ? (
            <DndListItem
              editable={editable}
              id={activeId}
              sx={{
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'primary',
                height: itemHeight,
                ...activeItem.containerSx
              }}
              variant={variant}
            >
              {activeItem.content}
            </DndListItem>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  )
}

export default DndList
